import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { JournalEntry, JournalEntryInsert, JournalEntrySearchCommand, JournalEntrySummary } from '../models/journal-entry/journal-entry';
import { PagedResult } from './bank-account.proxy.service';

/**
 * GL journal entries (#5024, #5025, #5026, #5077). Mostly read: entries are written by the posting services
 * as their source documents post. The exception is the manual screen (#5077), which creates and posts an
 * entry of its own — the three write methods at the bottom.
 *
 * The DTO is self-sufficient — account number and name are denormalized onto each line, so nothing here
 * needs a chart-of-accounts join.
 */
@Injectable({ providedIn: 'root' })
export class JournalEntryProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/journal-entries';

  public getById(id: string): Observable<ApiResponse<JournalEntry>> {
    return this.httpClient.get<ApiResponse<JournalEntry>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Looks an entry up by its number within a company. Used by the details page (#5026) to follow the
   * reversal cross-links, which name the other entry by number rather than by id.
   */
  public getByDocumentNumber(companyReference: string, documentNumber: string): Observable<ApiResponse<JournalEntry>> {
    return this.httpClient.get<ApiResponse<JournalEntry>>(
      `${this.baseUrl}/document/${companyReference}/${encodeURIComponent(documentNumber)}`,
    );
  }

  /**
   * Paged search for the dashboard (#5025). Company scope is deliberately omitted from the command — the
   * API fills it from the current site, so a forgotten field cannot widen the search to another company.
   */
  public search(
    command: JournalEntrySearchCommand,
    pageNumber = 1,
    pageSize = 25,
    sortField?: string | null,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<JournalEntry>>> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize).set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<JournalEntry>>>(`${this.baseUrl}/search`, command, { params });
  }

  /** The dashboard's four tiles in one call, scoped to the current site's company. */
  public getSummary(): Observable<ApiResponse<JournalEntrySummary>> {
    return this.httpClient.get<ApiResponse<JournalEntrySummary>>(`${this.baseUrl}/summary`);
  }

  /**
   * Creates a manual entry as a Draft (#5077). Nothing moves in the ledger until it is posted.
   *
   * The company is NOT sent: the API resolves it from the current site, the same way the search does, so a
   * forgotten field cannot write into another company's ledger.
   */
  public create(entry: JournalEntryInsert): Observable<ApiResponse<JournalEntry>> {
    return this.httpClient.post<ApiResponse<JournalEntry>>(this.baseUrl, entry);
  }

  /** Rewrites a Draft manual entry. Lines are replaced wholesale — an entry only means anything as a
   *  balanced set, so there is no half-updated state worth expressing. */
  public updateDraft(id: string, entry: JournalEntryInsert): Observable<ApiResponse<JournalEntry>> {
    return this.httpClient.put<ApiResponse<JournalEntry>>(`${this.baseUrl}/${id}`, entry);
  }

  /** Posts a Draft: balances move, the period must be open, and the entry becomes immutable. */
  public post(id: string): Observable<ApiResponse<JournalEntry>> {
    return this.httpClient.post<ApiResponse<JournalEntry>>(`${this.baseUrl}/${id}/post`, {});
  }
}
